import { Component, OnInit, ElementRef, ViewChild, signal, computed } from '@angular/core';
import { NavigationEnd, Router, RouterLink } from '@angular/router';
import { MatSnackBar } from '@angular/material/snack-bar';
import {
  Validators,
  UntypedFormBuilder,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
} from '@angular/forms';
import { environment } from '../../../environments/environment';
import { PublicSettings, User } from 'src/app/interfaces';
import {
  User as FirebaseUser,
  sendPasswordResetEmail,
  signInWithEmailAndPassword,
  signOut,
} from 'firebase/auth';
import { doc, getDoc } from 'firebase/firestore';
import { saveOrgInfoLocal } from 'src/app/globals';
import { auth, db } from 'src/app/app.component';
import { filter, Subscription } from 'rxjs';
import { NgStyle } from '@angular/common';
import { MatError, MatFormField, MatLabel, MatSuffix } from '@angular/material/form-field';
import { MatInput } from '@angular/material/input';
import { MatIconButton, MatButton } from '@angular/material/button';
import { MatTooltip } from '@angular/material/tooltip';
import { MatIcon } from '@angular/material/icon';
import { CustomValidators } from 'angular-custom-validators';
import { Title } from '@angular/platform-browser';

@Component({
  selector: 'app-login',
  templateUrl: './login.component.html',
  styleUrls: ['./login.component.scss'],
  imports: [
    NgStyle,
    FormsModule,
    ReactiveFormsModule,
    MatError,
    MatFormField,
    MatLabel,
    MatInput,
    MatIconButton,
    MatSuffix,
    MatTooltip,
    MatIcon,
    RouterLink,
    MatButton,
  ],
})
export class LoginComponent implements OnInit {
  orgId: string = localStorage.getItem('orgId');
  currentView = signal<'login' | 'reset-password'>('login');
  env = environment;
  publicSettings = signal<PublicSettings>(null);
  organisationName = computed(() => this.publicSettings()?.name || 'buurtvervoer');
  loginImage = computed(
    () =>
      this.publicSettings()?.loginImage || '../../../assets/default-images/default-login-image.svg',
  );
  logoImage = computed(
    () => this.publicSettings()?.logo || '../../../assets/default-images/default-logo.png',
  );
  faviconImage = computed(() => {
    if (this.publicSettings()?.favicon) {
      const favicon: HTMLLinkElement = document.querySelector('#favicon');
      favicon.href = <string>this.publicSettings()?.favicon;
      return this.publicSettings()?.favicon;
    }
    return null;
  });
  loading = signal(this.orgId ? true : false);
  loginForm: FormGroup;
  passwordResetForm: FormGroup;
  userData: User;
  showPasswords = signal(false);
  errorLoginMessage = signal<string>('');
  errorResetMessage = signal<string>('');
  sendingReset = signal(false);
  auth = auth;
  private routeSubscription: Subscription = new Subscription();

  @ViewChild('focusMeLogin', { static: false }) refLogin: ElementRef;

  constructor(
    public titleService: Title,
    private router: Router,
    private snackBar: MatSnackBar,
    private fb: UntypedFormBuilder,
  ) {}

  async ngOnInit() {
    // Subscribe to router navigation events to handle view changes
    this.routeSubscription = this.router.events
      .pipe(filter((event) => event instanceof NavigationEnd))
      .subscribe(() => {
        this.updateCurrentView();
      });

    // Set initial view
    this.updateCurrentView();

    this.loginForm = this.fb.group({
      email: ['', [Validators.required, CustomValidators.email]],
      password: ['', [Validators.required, Validators.minLength(6)]],
    });

    this.passwordResetForm = this.fb.group({
      email: ['', [Validators.required, CustomValidators.email]],
    });

    if (this.orgId) {
      console.log('Fetching public settings for orgId:', this.orgId);
      try {
        this.publicSettings.set(
          (
            await getDoc(doc(db, `organisations/${this.orgId}/settings/public`))
          ).data() as PublicSettings,
        );
        console.log('Fetched public settings:', this.publicSettings());
        const favicon: HTMLLinkElement = document.querySelector('#favicon');
        favicon.href = <string>this.publicSettings()?.favicon;
        this.titleService.setTitle(this.publicSettings()?.name || 'Buurtvervoer');
      } catch (error) {
        console.log('Error fetching public settings:', error);
        // Use default values if fetch fails
      }
      this.loading.set(false);
    } else {
      if (this.env.organisation.id) {
        localStorage.setItem('orgId', this.env.organisation.id);
        saveOrgInfoLocal(this.env.organisation.id);
        window.location.reload();
      }
    }

    if (auth.currentUser) {
      this.afterSignIn(auth.currentUser);
    }
  }

  ngOnDestroy(): void {
    this.routeSubscription.unsubscribe();
  }

  private updateCurrentView(): void {
    const url = this.router.url;

    if (url.includes('/login/reset-password')) {
      this.currentView.set('reset-password');
    } else {
      this.currentView.set('login');
    }

    this.loading.set(false);
  }

  async login() {
    if (!this.loginForm.valid) {
      this.loginForm.markAllAsTouched();
      return;
    }

    return signInWithEmailAndPassword(
      auth,
      this.loginForm.controls.email.value,
      this.loginForm.controls.password.value,
    )
      .then(async (userCredential) => this.afterSignIn(userCredential.user))
      .catch((error) => {
        const errorCode = error.code;
        const errorMessage = error.message;
        console.error('errorCode:', errorCode);
        console.error('errorMessage:', errorMessage);
        if (errorCode === 'auth/password-does-not-meet-requirements') {
          this.passwordResetForm.controls.email.setValue(
            this.loginForm.controls.email.value.trim(),
          );
          this.currentView.set('reset-password');
          this.errorResetMessage.set(
            'Je wachtwoord voldoet niet aan het wachtwoordbeleid. Vraag een resetlink aan.',
          );
          void this.router.navigate(['/login/reset-password']);
        } else if (
          errorCode === 'auth/user-not-found' ||
          errorCode === 'auth/wrong-password' ||
          errorCode === 'auth/invalid-credential'
        ) {
          this.errorLoginMessage.set('De combinatie van e-mail en wachtwoord is incorrect.');
        } else {
          this.errorLoginMessage.set('Er is een fout opgetreden, probeer het later opnieuw.');
        }
        this.loginForm.controls.password.reset();
        console.log(errorCode, errorMessage);
      });
  }

  async afterSignIn(user: FirebaseUser) {
    try {
      this.userData = {
        ...((await getDoc(doc(db, `users/${user.uid}`))).data() as User),
        id: `${user.uid}`,
      };
    } catch (error) {
      this.showSnackbar('Je hebt geen inlogrechten.');
      return this.signOutBackToLogin;
    }

    if (this.userData.organisations.length > 1) {
      if (this.userData.selectedOrgId) {
        return this.router.navigate([`/set/${this.userData.selectedOrgId}`]);
      }
      return this.router.navigate(['/selectingOrganisation']);
    } else if (this.userData.organisations.length === 1) {
      return this.router.navigate([`/set/${this.userData.organisations[0].id}`]);
    } else if (this.userData.organisations.length < 1) {
      this.showSnackbar('Je hebt geen organisatie.');
      return this.signOutBackToLogin;
    }
  }

  async resetPassword() {
    if (this.sendingReset()) return;
    const email = String(this.passwordResetForm.controls.email.value || '').trim();
    this.passwordResetForm.controls.email.setValue(email);
    if (!this.passwordResetForm.valid) {
      this.passwordResetForm.markAllAsTouched();
      this.errorResetMessage.set('Vul een geldig e-mailadres in om het wachtwoord te resetten.');
      return;
    }

    this.sendingReset.set(true);
    this.errorResetMessage.set('');
    try {
      this.auth.languageCode = 'nl';
      await sendPasswordResetEmail(this.auth, email, {
        url: `${window.location.origin}/login`,
      });
      this.showResetConfirmation();
    } catch (error) {
      const code = (error as { code?: string }).code;
      if (code === 'auth/user-not-found' || code === 'auth/invalid-credential') {
        this.showResetConfirmation();
      } else {
        this.errorResetMessage.set('Er is een fout opgetreden, probeer het later opnieuw.');
      }
    } finally {
      this.sendingReset.set(false);
    }
  }

  private showResetConfirmation() {
    this.showSnackbar(
      'Er wordt een wachtwoord reset link verstuurd indien er een account bekend is op het e-mailadres.',
    );
  }

  signOutBackToLogin() {
    signOut(this.auth)
      .then(() => {
        return this.router.navigate(['/login']);
      })
      .catch((error) => {
        console.log('logoutError', error);
      });
  }

  showSnackbar(txt: string) {
    this.snackBar.open(txt, 'X', {
      duration: 5000,
    });
  }
}
